import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import {
  getSmartRoutingSuggestions,
  triggerSmartRouting,
  dispatchSmartRoute,
} from "../../api/smartRouting";

export const fetchSmartRoutingSuggestions = createAsyncThunk(
  "smartRouting/fetchSuggestions",
  async (_, { rejectWithValue }) => {
    try {
      const { data } = await getSmartRoutingSuggestions();
      return data;
    } catch (e) {
      return rejectWithValue(e.response?.data || { error: e.message });
    }
  }
);

export const triggerSmartRoutingRecalc = createAsyncThunk(
  "smartRouting/triggerRecalc",
  async (_, { dispatch, rejectWithValue }) => {
    try {
      await triggerSmartRouting();
      // Lambda is async — give it a beat then refresh.
      setTimeout(() => dispatch(fetchSmartRoutingSuggestions()), 2500);
      return true;
    } catch (e) {
      return rejectWithValue(e.response?.data || { error: e.message });
    }
  }
);

export const dispatchSmartRoutingRoute = createAsyncThunk(
  "smartRouting/dispatchRoute",
  async (payload, { dispatch, rejectWithValue }) => {
    try {
      const { data } = await dispatchSmartRoute(payload);
      dispatch(fetchSmartRoutingSuggestions());
      return data;
    } catch (e) {
      return rejectWithValue(e.response?.data || { error: e.message });
    }
  }
);

const initialState = {
  loading: false,
  recalculating: false,
  dispatchingClusterId: null,
  data: null, // { calculation_id, status, created_at, data: { routes, solo_pending, origin } }
  error: null,
  // Operator-side mutations that aren't persisted server-side until dispatch:
  routeOverrides: {}, // { [clusterId]: [orderId, ...] }
  driverSelections: {}, // { [clusterId]: driver_id }
  activeRouteIdx: 0,
};

const smartRoutingSlice = createSlice({
  name: "smartRouting",
  initialState,
  reducers: {
    setActiveRoute(state, action) {
      state.activeRouteIdx = action.payload;
    },
    selectDriver(state, action) {
      const { clusterId, driverId } = action.payload;
      state.driverSelections[clusterId] = driverId;
    },
    moveOrderToRoute(state, action) {
      const { orderId, targetClusterId } = action.payload;
      const calc = state.data?.data;
      if (!calc?.routes) return;

      // remove orderId from any existing override or original cluster
      calc.routes.forEach((r) => {
        const current =
          state.routeOverrides[r.cluster_id] ||
          r.orders.map((o) => o.order_id);
        const next = current.filter((id) => id !== orderId);
        if (next.length !== current.length) {
          state.routeOverrides[r.cluster_id] = next;
        }
      });

      if (targetClusterId !== "solo") {
        const targetRoute = calc.routes.find(
          (r) => r.cluster_id === targetClusterId
        );
        const current =
          state.routeOverrides[targetClusterId] ||
          (targetRoute
            ? targetRoute.orders.map((o) => o.order_id)
            : []);
        if (!current.includes(orderId)) {
          state.routeOverrides[targetClusterId] = [...current, orderId];
        }
      }
    },
    clearOverrides(state) {
      state.routeOverrides = {};
      state.driverSelections = {};
      state.activeRouteIdx = 0;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchSmartRoutingSuggestions.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchSmartRoutingSuggestions.fulfilled, (state, action) => {
        state.loading = false;
        const newCalcId = action.payload?.calculation_id;
        const oldCalcId = state.data?.calculation_id;
        state.data = action.payload;
        // Drop overrides if the suggestion set changed under the operator
        if (newCalcId && newCalcId !== oldCalcId) {
          state.routeOverrides = {};
          state.driverSelections = {};
          state.activeRouteIdx = 0;
        }
      })
      .addCase(fetchSmartRoutingSuggestions.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(triggerSmartRoutingRecalc.pending, (state) => {
        state.recalculating = true;
      })
      .addCase(triggerSmartRoutingRecalc.fulfilled, (state) => {
        state.recalculating = false;
      })
      .addCase(triggerSmartRoutingRecalc.rejected, (state) => {
        state.recalculating = false;
      })
      .addCase(dispatchSmartRoutingRoute.pending, (state, action) => {
        state.dispatchingClusterId = action.meta.arg.cluster_id;
      })
      .addCase(dispatchSmartRoutingRoute.fulfilled, (state) => {
        state.dispatchingClusterId = null;
      })
      .addCase(dispatchSmartRoutingRoute.rejected, (state) => {
        state.dispatchingClusterId = null;
      });
  },
});

export const {
  setActiveRoute,
  selectDriver,
  moveOrderToRoute,
  clearOverrides,
} = smartRoutingSlice.actions;

/* ────── selectors ────── */

export const selectSmartRoutingCalculation = (state) =>
  state.smartRouting.data?.data || null;

export const selectSmartRoutingRoutes = (state) => {
  const calc = state.smartRouting.data?.data;
  if (!calc?.routes) return [];
  const orderMap = new Map();
  calc.routes.forEach((rr) =>
    (rr.orders || []).forEach((o) => orderMap.set(o.order_id, o))
  );
  (calc.solo_pending || []).forEach((o) =>
    orderMap.set(o.order_id, o)
  );
  return calc.routes.map((r, idx) => {
    const override = state.smartRouting.routeOverrides[r.cluster_id];
    const orderIds = override || (r.orders || []).map((o) => o.order_id);
    const orders = orderIds
      .map((id) => orderMap.get(id))
      .filter((o) => o && Number.isFinite(Number(o.lat)) && Number.isFinite(Number(o.lon)));
    return { ...r, idx, orders, isOverridden: !!override };
  });
};

export const selectSmartRoutingSoloOrders = (state) => {
  const calc = state.smartRouting.data?.data;
  if (!calc) return [];
  const routes = selectSmartRoutingRoutes(state);
  const inAnyRoute = new Set();
  routes.forEach((r) => r.orders.forEach((o) => inAnyRoute.add(o.order_id)));
  const all = [
    ...(calc.solo_pending || []),
    ...(calc.routes || []).flatMap((r) => r.orders || []),
  ];
  const seen = new Set();
  return all.filter((o) => {
    if (!o || !Number.isFinite(Number(o.lat)) || !Number.isFinite(Number(o.lon))) return false;
    if (inAnyRoute.has(o.order_id)) return false;
    if (seen.has(o.order_id)) return false;
    seen.add(o.order_id);
    return true;
  });
};

export const selectSmartRoutingOrigin = (state) => {
  const o = state.smartRouting.data?.data?.origin;
  if (o && Number.isFinite(Number(o.lat)) && Number.isFinite(Number(o.lon))) {
    return [Number(o.lat), Number(o.lon)];
  }
  return null;
};

export const selectSmartRoutingRouteCount = (state) =>
  selectSmartRoutingRoutes(state).length;

export default smartRoutingSlice.reducer;
